import { AI_ASSISTANT_APP_ID, AI_ASSISTANT_URL } from '../config/environment';
import { apiRequest, streamSseRequest } from './apiClient';

export type InsightCard = {
  id: string;
  type: string;
  title: string;
  summary: string;
  metrics?: Record<string, unknown> | null;
  period?: string | null;
  ctaPrompt: string;
  viewScope?: string;
  summaryBullets?: string[];
  widgetTargets?: string[];
  /** Widget this card was generated for (drill-down target). */
  primaryWidget?: string | null;
  /** Home strip index membership (≤3, deduped by userQuestion). */
  featuredOnStrip?: boolean;
  /** Higher = show earlier (impact × specificity × recency × actionability × confidence). */
  priority?: number;
  /** P0/P1 user question this card answers. */
  userQuestion?: string;
  /** Display OIIA from generate — list-safe, not the full contextPayload. */
  observation?: string;
  interpretation?: string;
  impact?: string;
  action?: string;
  /** Present on GET /insights/{id} — opaque payload for ai-assistant. */
  contextPayload?: Record<string, unknown>;
  createdAt: string;
};

/** Compact finance snapshot from budget-server — pushed into ai-assistant threads. */
export type AssistantContextSnapshot = Record<string, unknown>;

export type AssistantMessage = {
  /** Stable id from ai-assistant (required to pin). */
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
};

export type AssistantThread = {
  id: string;
  appId?: string;
  title?: string;
  metadata?: Record<string, unknown>;
  hasContext?: boolean;
  messages: AssistantMessage[];
  updatedAt?: string;
};

export type AssistantThreadSummary = {
  id: string;
  appId?: string;
  title?: string;
  updatedAt: string;
};

/**
 * Insights remain on budget-server (summaries for free + Pro; no contextPayload).
 * Optional `widget` filters by `widgetTargets`. Chat grounding uses getInsight (Pro).
 */
export async function listInsights(
  view: string = 'personal',
  opts?: { widget?: string },
): Promise<{
  view: string;
  widget?: string;
  insights: InsightCard[];
}> {
  const params = new URLSearchParams({ view });
  if (opts?.widget) params.set('widget', opts.widget);
  return apiRequest(`/insights?${params.toString()}`);
}

/** Full insight card including contextPayload for thread grounding. */
export async function getInsight(
  insightId: string,
  view: string = 'personal',
): Promise<{ view: string; insight: InsightCard }> {
  return apiRequest(`/insights/${encodeURIComponent(insightId)}?view=${encodeURIComponent(view)}`);
}

export async function refreshInsights(): Promise<{ insights: InsightCard[]; refreshed: boolean }> {
  return apiRequest('/insights/refresh', { method: 'POST' });
}

/**
 * Compact household finance snapshot (Pro). Client pushes this into ai-assistant —
 * the assistant never pulls from budget-server.
 */
export async function getAssistantContext(view: string = 'personal'): Promise<{
  view: string;
  context: AssistantContextSnapshot;
}> {
  return apiRequest(`/assistant/context?view=${encodeURIComponent(view)}`);
}

/**
 * Build opaque context blob for ai-assistant: snapshot + optional insight payload.
 * Strips obsolete savings-rate fields so the model cannot misquote them.
 */
export function buildPushedAssistantContext(params: {
  snapshot: AssistantContextSnapshot;
  insight?: InsightCard | null;
}): Record<string, unknown> {
  const { snapshot, insight } = params;
  const cleaned = stripLegacySavingsRateFields(snapshot as Record<string, unknown>);
  if (!insight) {
    return { snapshot: cleaned };
  }
  const payload = insight.contextPayload ?? {};
  return {
    snapshot: cleaned,
    insight: {
      id: insight.id,
      type: insight.type,
      title: insight.title,
      summary: insight.summary,
      userQuestion: insight.userQuestion,
      ctaPrompt: insight.ctaPrompt,
      metrics: insight.metrics ?? null,
      period: insight.period ?? null,
      // OIIA is chat-only — never re-sent as card bullets.
      observation: payload.observation,
      interpretation: payload.interpretation,
      impact: payload.impact,
      action: payload.action,
      contextPayload: payload,
    },
  };
}

const LEGACY_SAVINGS_RATE_KEYS = new Set([
  'savingsRateEstimate',
  'savingsRatePct',
  'savingsRateLabel',
  'savingsRateTarget',
  'savings',
]);

function stripLegacySavingsRateFields(
  value: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value)) {
    if (LEGACY_SAVINGS_RATE_KEYS.has(key)) continue;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[key] = stripLegacySavingsRateFields(v as Record<string, unknown>);
    } else {
      out[key] = v;
    }
  }
  return out;
}

function threadsBase(): string {
  return `/apps/${AI_ASSISTANT_APP_ID}/threads`;
}

function assistantRequest<T>(endpoint: string, options: Parameters<typeof apiRequest>[1] = {}) {
  return apiRequest<T>(endpoint, { ...options, baseUrl: AI_ASSISTANT_URL });
}

/** List previous chats (ChatGPT-style history). */
export async function listAssistantThreads(): Promise<{ threads: AssistantThreadSummary[] }> {
  return assistantRequest(threadsBase());
}

export async function createAssistantThread(params: {
  title?: string;
  /** Opaque context from budget-server (or any app) — pushed into the assistant. */
  context?: string | Record<string, unknown>;
  metadata?: Record<string, unknown>;
  /** @deprecated prefer metadata.sourceInsightId */
  insightId?: string;
}): Promise<{ thread: AssistantThread }> {
  const metadata = {
    ...(params.metadata ?? {}),
    ...(params.insightId ? { sourceInsightId: params.insightId } : {}),
  };
  return assistantRequest(threadsBase(), {
    method: 'POST',
    body: JSON.stringify({
      title: params.title,
      context: params.context,
      metadata: Object.keys(metadata).length ? metadata : undefined,
    }),
  });
}

/** Push/replace user-level context before chatting. */
export async function putAssistantThreadContext(
  threadId: string,
  context: string | Record<string, unknown>,
): Promise<{ thread: AssistantThread }> {
  return assistantRequest(`${threadsBase()}/${threadId}/context`, {
    method: 'PUT',
    body: JSON.stringify({ context }),
  });
}

export async function getAssistantThread(threadId: string): Promise<{ thread: AssistantThread }> {
  return assistantRequest(`${threadsBase()}/${threadId}`);
}

export async function renameAssistantThread(
  threadId: string,
  title: string,
): Promise<{ thread: AssistantThreadSummary }> {
  return assistantRequest(`${threadsBase()}/${threadId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}

export async function deleteAssistantThread(threadId: string): Promise<{ ok: boolean }> {
  return assistantRequest(`${threadsBase()}/${threadId}`, { method: 'DELETE' });
}

export type StreamAssistantMessageResult = {
  messageId?: string;
  content: string;
};

/**
 * ChatGPT-style token stream — SSE deltas from ai-assistant.
 * Events: `{ type: "delta", text }` then `{ type: "done", messageId }`.
 */
export async function streamAssistantMessage(
  threadId: string,
  content: string,
  opts: {
    context?: string | Record<string, unknown>;
    signal?: AbortSignal;
    onDelta: (text: string) => void;
  },
): Promise<StreamAssistantMessageResult> {
  let assembled = '';
  let messageId: string | undefined;

  await streamSseRequest(`${threadsBase()}/${threadId}/messages/stream`, {
    baseUrl: AI_ASSISTANT_URL,
    method: 'POST',
    body: JSON.stringify({ content, context: opts.context }),
    signal: opts.signal,
    onEvent: (event) => {
      const type = event.type;
      if (type === 'delta') {
        const text =
          typeof event.text === 'string'
            ? event.text
            : typeof event.content === 'string'
              ? event.content
              : '';
        if (!text) return;
        assembled += text;
        opts.onDelta(text);
        return;
      }
      if (type === 'done') {
        if (typeof event.messageId === 'string') messageId = event.messageId;
        if (typeof event.content === 'string' && event.content && !assembled) {
          assembled = event.content;
          opts.onDelta(event.content);
        }
      }
    },
  });

  return { messageId, content: assembled };
}

/** Non-stream fallback (wait for full reply). Prefer `streamAssistantMessage`. */
export async function postAssistantMessage(
  threadId: string,
  content: string,
  context?: string | Record<string, unknown>,
): Promise<{ message: AssistantMessage; threadId: string }> {
  return assistantRequest(`${threadsBase()}/${threadId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ content, context }),
  });
}
